import React from 'react';
import { motion } from 'framer-motion';
import { DivideIcon as LucideIcon } from 'lucide-react';

interface AppleIconProps {
  icon: LucideIcon;
  gradient?: {
    from: string;
    to: string;
    darkFrom?: string;
    darkTo?: string;
  };
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  label?: string;
  className?: string;
}

const AppleIcon: React.FC<AppleIconProps> = ({
  icon: Icon,
  gradient = {
    from: 'from-blue-400',
    to: 'to-blue-600',
    darkFrom: 'dark:from-blue-500',
    darkTo: 'dark:to-blue-700'
  },
  size = 'md',
  onClick,
  label,
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  const iconSizes = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`relative group ${className}`}
    >
      {/* Icon Container */}
      <div
        className={`
          ${sizeClasses[size]}
          relative
          rounded-lg
          bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo}
          flex items-center justify-center
          shadow-lg
          transition-all duration-300
          group-hover:shadow-xl
          group-hover:translate-y-[-2px]
          group-active:shadow-md
          group-active:translate-y-[0px]
        `}
      >
        {/* Glass Effect */}
        <div className="absolute inset-0 rounded-lg bg-white/10 dark:bg-black/10" />
        
        {/* Highlight Effect */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 to-transparent dark:from-white/10" />
        
        {/* Icon */}
        <Icon className={`${iconSizes[size]} text-white relative z-10`} />
      </div>

      {/* Label */}
      {label && (
        <span className="block text-center mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </span>
      )}
    </motion.button>
  );
};

export default AppleIcon;